import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { get } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Link from 'components/common/Link';

import IconButton from 'components/common/IconButton';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';

import formatSinglePlural from 'utils/formatSinglePlural';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';
import formatOrderNumber from 'utils/formatOrderNumber';
import formatPrice from 'utils/formatPrice';

import { fetchEventLogItemDiff } from 'redux/modules/eventLog';

import timeDateFormat from 'config/timeDateFormat';
import dateFormat from 'config/dateFormat';

const styles = theme => ({
  button: {
    margin: -16,
    verticalAlign: 'middle',
  },
  iconArrowHeadBottom: {
    width: 12,
    height: 8,
  },
  iconArrowHeadBottomDiffOpen: {
    transform: 'rotate(180deg)',
  },
  tableCellDate: {
    width: 120,
    whiteSpace: 'nowrap',
  },
  tableCellDiffType: {
    width: 120,
    textAlign: 'right',
    textTransform: 'uppercase',
    verticalAlign: 'top',
  },
  tableCellActions: {
    width: 16,
    paddingRight: 29,
    '&:last-child': {
      paddingRight: 29,
      [theme.breakpoints.down('sm')]: {
        paddingRight: 16,
      },
    },
  },
  tableRow: {
    '&:first-child $tableCell': {
      borderTop: 'none',
    },
  },
  tableRowDiffOpen: {
    height: 35,
  },
  tableCell: {
    borderBottom: 'none',
    borderTop: `1px solid ${theme.palette.primary.A400}`,
  },
  tableCellDiffOpen: {
    paddingBottom: 0,
  },
  tableRowDiff: {
    height: 29,
  },
  tableCellDiff: {
    paddingTop: 0,
    paddingBottom: 0,
    verticalAlign: 'top',
    color: theme.palette.grey[700],
    borderBottom: 'none',
  },
  diffTypeHidden: {
    visibility: 'hidden',
  },
  fieldName: {
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  fieldValue: {
    fontWeight: '500',
  },
});

class EventLogViewTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      diffOpen: false,
      newDiffOpen: false,
    };

    this.onDiffButtonClick = this.onDiffButtonClick.bind(this);
    this.onNewDiffButtonClick = this.onNewDiffButtonClick.bind(this);
  }

  onDiffButtonClick() {
    const { actions, Diff } = this.props;
    if (!Diff) {
      actions.fetchEventLogItemDiff({
        'Diff ID': this.props['Diff ID'],
      });
    }
    this.setState({ diffOpen: !this.state.diffOpen });
  }

  onNewDiffButtonClick() {
    this.setState({ newDiffOpen: !this.state.newDiffOpen });
  }

  render() {
    const {
      Event,
      User,
      Diff,
      business, 
      ID,
      classes,
      ...otherProps
    } = this.props;
    let newEventLogItem = false;
    const { diffOpen, newDiffOpen } = this.state;
    let message;
    const userName = `${User['First Name']} ${User['Last Name']}`;
    const eventParams = otherProps['Event Params'];
    const renderFieldName = fieldName => (
      typeof fieldName === 'string'
        ? `"${fieldName}"`.replace('Contact Information.', '')
          .replace('Insurance Info.', 'Insurance Info ')
          .replace('Address Information.', '')
          .replace('Change Info.', 'Change Info ')
        : [
          ...fieldName.slice(0, fieldName.length - 2),
          `number ${fieldName[fieldName.length - 2] + 1}`,
          `"${fieldName[fieldName.length - 1]}"`,
        ].join(' '));
    switch (`${Event} ${eventParams.Type}`) {
      // Patient
      case 'Create patient':
        message = `${userName} created the chart`;
        break;
      case 'Delete patient':
        message = `${userName} deleted the chart`;
        break;
      case 'View patient':
        message = `${userName} accessed the chart`;
        break;
      case 'Update patient': {
        if (eventParams['Change Fields']) {
          const fields = eventParams['Change Fields']
            .map(item => renderFieldName(item));
          const fieldsLabel = formatSinglePlural({
            quantity: fields.length,
            single: 'field',
            plural: 'fields',
          });
          message = `${userName}
          updated ${fields.join(', ')} ${fieldsLabel}`;
        } else {
          message = `${userName} updated the chart`;
        }
        break;
      }
      // Patient SMS
      case 'Create SMS to Patient': {
        const messageType = eventParams['Document ID'] ? 'MMS' : 'SMS';
        message = eventParams.Message ? `${userName} sent ${messageType} to patient: ${eventParams.Message}` : `${userName} sent ${messageType} to patient`;
        break;
      }
      case 'Receive SMS from Patient': {
        const messageType = eventParams['Document ID'] ? 'MMS' : 'SMS';
        message = eventParams.Message ? `New ${messageType} from patient: ${eventParams.Message}` : `New ${messageType} from patient`;
        break;
      }
      // Remind
      case 'Remind Date of Service Reminder': {
        const daysLabel = formatSinglePlural({
            quantity: eventParams['Days Left'],
            single: 'day',
            plural: 'days',
          });   
        message = (
          <Fragment>
            {`Date of Service expires in ${eventParams['Days Left']} ${daysLabel} in order`}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      }
      case 'Remind Authorization Date Reminder': {
        const daysLabel = formatSinglePlural({
            quantity: eventParams['Days Left'],
            single: 'day',
            plural: 'days',
          });   
        message = (
          <Fragment>
            {`Authorization expires in ${eventParams['Days Left']} ${daysLabel} in order`}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      }
      // Insurance
      case 'Create insurance':
        message = `${userName}
        created "${eventParams['Payer Name']}" insurance with ID "${eventParams['Insurance ID']}"`;
        break;
      case 'Update insurance': {
        const fields = eventParams['Change Fields']
          .filter(item => item.indexOf('Insurance Info.') === -1)
          .map(item => renderFieldName(item));
        const fieldsLabel = formatSinglePlural({
          quantity: fields.length,
          single: 'field',
          plural: 'fields',
        });
        message = `${userName}
        updated ${fields.join(', ')} ${fieldsLabel} in insurance "${eventParams['Insurance ID']}"`;
        break;
      }
      case 'Check eligibility':
        message = `${userName}
        checked eligibility of insurance "${eventParams['Insurance ID']}"`;
        break;
      case 'Delete insurance':
        message = `${userName} deleted insurance "${eventParams['Insurance ID']}"`;
        break;
      // Credit Card
      case 'Create credit_card':
        message = `${userName}
        created credit card "${eventParams['Card Number']}"`;
        break;
      case 'Update credit_card':
        message = `${userName} set credit card "${eventParams['Card Number']}" as default`;
        break;
      case 'Delete credit_card':
        message = `${userName} deleted credit card "${eventParams['Card Number']}"`;
        break;
      // Referral Source
      case 'Add Resource referral_source':
        message = `${userName}
        added referral source "${eventParams['Resource Info']['Name of source']}" to patient`;
        break;
      case 'Update referral_source': {
        const fields = eventParams['Change Fields']
          .map(item => `"${item}"`);
        const fieldsLabel = formatSinglePlural({
          quantity: fields.length,
          single: 'field',
          plural: 'fields',
        });
        message = `${userName}
        updated ${fields.join(', ')} ${fieldsLabel} in referral source "${eventParams['Name of source']}"`;
        break;
      }
      case 'Remove Resource referral_source':
        message = `${userName} removed referral source
        "${eventParams['Resource Info']['Name of source']}"
        from patient`;
        break;
      case 'Delete referral_source':
        message = `${userName} deleted referral source "${eventParams['Name of source']}"`;
        break;
      // Sales Representative
      case 'Add Resource sales_representative':
        message = `${userName}
        added sales representative
        "${eventParams['Resource Info']['First Name']} ${eventParams['Resource Info']['Last Name']}"
        to patient`;
        break;
      case 'Update sales_representative': {
        const fields = eventParams['Change Fields']
          .map(item => `"${item}"`);
        const fieldsLabel = formatSinglePlural({
          quantity: fields.length,
          single: 'field',
          plural: 'fields',
        });
        message = `${userName}
        updated ${fields.join(', ')} ${fieldsLabel} in sales representative
        "${eventParams['First Name']} ${eventParams['Last Name']}"`;
        break;
      }
      case 'Remove Resource sales_representative':
        message = `${userName} removed sales representative
        "${eventParams['Resource Info']['First Name']} ${eventParams['Resource Info']['Last Name']}"
        from patient`;
        break;
      case 'Delete sales_representative':
        message = `${userName} deleted sales representative
        "${eventParams['First Name']} ${eventParams['Last Name']}"`;
        break;
      // Sales Prescriber
      case 'Add Resource prescriber':
        message = `${userName}
        added prescriber
        "${eventParams['Resource Info']['First Name']} ${eventParams['Resource Info']['Last Name']}"
        to patient`;
        break;
      case 'Update prescriber': {
        const fields = eventParams['Change Fields']
          .map(item => `"${item}"`);
        const fieldsLabel = formatSinglePlural({
          quantity: fields.length,
          single: 'field',
          plural: 'fields',
        });
        message = `${userName}
        updated ${fields.join(', ')} ${fieldsLabel} in prescriber
        "${eventParams['First Name']} ${eventParams['Last Name']}"`;
        break;
      }
      case 'Remove Resource prescriber':
        message = `${userName} removed prescriber
        "${eventParams['Resource Info']['First Name']} ${eventParams['Resource Info']['Last Name']}"
        from patient`;
        break;
      case 'Delete prescriber':
        message = `${userName} deleted prescriber
        "${eventParams['First Name']} ${eventParams['Last Name']}"`;
        break;
      // Vitals
      case 'Create vitals':
        message = `${userName}
        created vitals:
        weight "${eventParams.Weight} lbs",
        height: "${eventParams['Height Ft']}ft, ${eventParams['Height In']}in",
        BMI "${eventParams.BMI}"`;
        break;
      case 'Delete vitals':
        message = `${userName}
        deleted vitals:
        weight "${eventParams.Weight} lbs",
        height: "${eventParams['Height Ft']}ft, ${eventParams['Height In']}in",
        BMI "${eventParams.BMI}"`;
        break;
      // Diagnosis
      case 'Add Resource diagnosis':
        message = `${userName}
        added diagnosis ${eventParams['Resource Info']['DX Code']}
        "${eventParams['Resource Info']['Medium Description']}" to patient`;
        break;
      case 'Remove Resource diagnosis':
        message = `${userName}
        removed diagnosis ${eventParams['Resource Info']['DX Code']}
        "${eventParams['Resource Info']['Medium Description']}" from patient`;
        break;
      // Notes
      case 'Create Patient Notes':
      case 'Create notes':
        message = `${userName}
        created note "${eventParams.Text}"`;
        break;
      case 'Update Patient Notes':
      case 'Update notes':
        message = `${userName}
        updated note "${eventParams.Text}"`;
        break;
      case 'Delete Patient Notes':
      case 'Delete notes':
        message = `${userName}
        deleted note "${eventParams.Text}"`;
        break;
      // Document
      case 'Add Resource document':
        message = `${userName}
        attached document "${eventParams['Resource Info']['File Name']}" to patient`;
        break;
      case 'Delete document':
        message = `${userName}
        deleted document "${eventParams['File Name']}"`;
        break;
      case 'Create Patient Document':
        message = `${userName}
        attached document "${eventParams['File Name']}" to patient`;
        break;
      case 'Delete Patient Document':
        message = `${userName}
        deleted document "${eventParams['File Name']}"`;
        break;
      case 'View order':
        message = (
          <Fragment>
            {`${userName} accessed order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Create Order Notes':
        message = (
          <Fragment>
            {`${userName} created note "${eventParams.Text}" in order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Update Order Notes':
        message = (
          <Fragment>
            {`${userName} updated note "${eventParams.Text}" in order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Delete Order Notes':
        message = (
          <Fragment>
            {`${userName} deleted note "${eventParams.Text}" in order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Update order':
      case 'Create order': {
        if (!eventParams['Change Fields'] && Event === 'Create') {
          const orders = typeof eventParams['Order ID'] === 'object' ? eventParams['Order ID'] : [eventParams['Order ID']];
          const ordersLabel = formatSinglePlural({
            quantity: orders.length,
            single: 'order',
            plural: 'orders',
          });
          message = `${userName}
          created ${ordersLabel} ${orders.join(', ')}`;
          message = (
            <Fragment>
              {`${userName} created ${ordersLabel} `}
              {orders.map((item, index) => (
                <Fragment key={item}>
                  <Link
                    color="primary"
                    breakWord
                    href={`/workflow/order/edit/${item}`}
                    target="_blank"
                  >
                    {`"${formatOrderNumber(item)}"`}
                  </Link>
                  {index + 1 < orders.length ? ', ' : ''}
                </Fragment>
              ))}
            </Fragment>
          );
        } else {
          newEventLogItem = true;
          const getMessage = ({ field, fieldData, HCPCCode, payerName }) => {
            const fieldName = `"${field.replace(new RegExp("_", "g"), ' ')}"`; 
            let before = fieldData.before ? `"${fieldData.before}"` : null;
            let after = fieldData.after ? `"${fieldData.after}"` : null;
            if (field === 'reimburse') {
              before = fieldData.before
                ? `"${formatPrice(fieldData.before, true)}"`
                : null;
              after = fieldData.after
                ? `"${formatPrice(fieldData.after, true)}"`
                : null;
            }
            if (field === 'reimburse_date') {
              before = fieldData.before
                ? `"${formatUtcToLocalDate(
                  fieldData.before,
                  timeDateFormat,
                  dateFormat,
                  business['Company Address']['Time Zone'],
                  business['Company Address']['Observes Daylight Saving'],
                )}"`
                : null;
              after = fieldData.after
                ? `"${formatUtcToLocalDate(
                  fieldData.after,
                  timeDateFormat,
                  dateFormat,
                  business['Company Address']['Time Zone'],
                  business['Company Address']['Observes Daylight Saving'],
                )}"`
                : null;
            }
            if (field === 'workflow_status') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem => beforeItem.Name).join(', ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem => afterItem.Name).join(', ')}"`
                : null;
            }
            if (field === 'diagnosis' || field === 'primary_diagnosis') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem => beforeItem['DX Code']).join(', ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem => afterItem['DX Code']).join(', ')}"`
                : null;
            }
            if (field === 'modifiers') {
              before = fieldData.before
                ? `"${['', '', '', ''].map((modifiersItem, index) =>
                  fieldData.before.slice(index * 2, (index * 2) + 2)).filter(beforeItem => beforeItem).join('-')}"`
                : null;
              after = fieldData.after
                ? `"${['', '', '', ''].map((modifiersItem, index) =>
                  fieldData.after.slice(index * 2, (index * 2) + 2)).filter(afterItem => afterItem).join('-')}"`
                : null;
            }
            if (field === 'assigned_user' || field === 'assigned_from_user') {
              before = fieldData.before[0] && fieldData.before[0].ID ? `"${fieldData.before.map(beforeItem =>
                `${beforeItem['First Name']} ${beforeItem['Last Name']}`).join(', ')}"` : null;
              after = fieldData.after[0] && fieldData.after[0].ID ? `"${fieldData.after.map(afterItem =>
                `${afterItem['First Name']} ${afterItem['Last Name']}`).join(', ')}"` : null;
            }
            if (field === 'patient') {
              before = fieldData.before[0] && fieldData.before[0]['Record Number'] ? `"${fieldData.before.map(beforeItem =>
                `${beforeItem['First Name']} ${beforeItem['Last Name']}`).join(', ')}"` : null;
              after = fieldData.after[0] && fieldData.after[0]['Record Number'] ? `"${fieldData.after.map(afterItem =>
                `${afterItem['First Name']} ${afterItem['Last Name']}`).join(', ')}"` : null;
            }
            if (field === 'cmn') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem => beforeItem['File Name']).join(', ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem => afterItem['File Name']).join(', ')}"`
                : null;
            }
            if (field === 'date_of_service_from' || field === 'date_of_service_to') {
              before = fieldData.before
                ? `"${formatUtcToLocalDate(
                    fieldData.before,
                    dateFormat,
                    dateFormat,
                    business['Company Address']['Time Zone'],
                    business['Company Address']['Observes Daylight Saving'],
                  )}"`
                : null;
              after = fieldData.after
                ? `"${formatUtcToLocalDate(
                    fieldData.after,
                    dateFormat,
                    dateFormat,
                    business['Company Address']['Time Zone'],
                    business['Company Address']['Observes Daylight Saving'],
                  )}"`
                : null;
            }
            if (field === 'charge' || field === 'cost') {
              before = fieldData.before
                ? `"${formatPrice(fieldData.before, true)}"`
                : null;
              after = fieldData.after
                ? `"${formatPrice(fieldData.after, true)}"`
                : null;
            }
            if (field === 'date_of_delivery') {
              before = fieldData.before
                ? `"${formatUtcToLocalDate(
                    fieldData.before,
                    timeDateFormat,
                    timeDateFormat,
                    business['Company Address']['Time Zone'],
                    business['Company Address']['Observes Daylight Saving'],
                  )}"`
                : null;
              after = fieldData.after
                ? `"${formatUtcToLocalDate(
                    fieldData.after,
                    timeDateFormat,
                    timeDateFormat,
                    business['Company Address']['Time Zone'],
                    business['Company Address']['Observes Daylight Saving'],
                  )}"`
                : null;
            }
            if (field === 'driver') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem =>
                  `${beforeItem['First Name']} ${beforeItem['Last Name']}`).join(', ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem =>
                  `${afterItem['First Name']} ${afterItem['Last Name']}`).join(', ')}"`
                : null;
            }
            if (field === 'insurance' || field === 'primary_insurance') {
              before = fieldData.before[0] && fieldData.before[0].ID
                ? `"${fieldData.before.map(beforeItem => beforeItem['Payer Name']).join(', ')}"` : null;
              after = fieldData.after[0] && fieldData.after[0].ID
                ? `"${fieldData.after.map(afterItem => afterItem['Payer Name']).join(', ')}"` : null;
            }
            if (field === 'facility') {
              before = fieldData.before[0] && fieldData.before[0].ID
                ? `"${fieldData.before.map(beforeItem => beforeItem.name).join(', ')}"`
                : null;
              after = fieldData.after[0] && fieldData.after[0].ID
                ? `"${fieldData.after.map(afterItem => afterItem.name).join(', ')}"`
                : null;
            }
            if (field === 'referral_source') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem => beforeItem['Name of source']).join(', ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem => afterItem['Name of source']).join(', ')}"`
                : null;
            }
            if (field === 'sales_representative' || field === 'prescriber') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem =>
                  `${beforeItem['First Name']} ${beforeItem['Last Name']}`).join(', ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem =>
                  `${afterItem['First Name']} ${afterItem['Last Name']}`).join(', ')}"`
                : null;
            }
            if (field === 'diagnosis' || field === 'primary_diagnosis') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem => beforeItem['DX Code']).join(', ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem => afterItem['DX Code']).join(', ')}"`
                : null;
            }
            if (field === 'vitals') {
              before = fieldData.before.filter(beforeItem => beforeItem).length > 0
                ? `"${fieldData.before.map(beforeItem =>
                  `Weight "${beforeItem['Weight']}", Height "${beforeItem['Height Ft']}ft, ${beforeItem['Height In']}in"`).join('; ')}"`
                : null;
              after = fieldData.after.filter(afterItem => afterItem).length > 0
                ? `"${fieldData.after.map(afterItem =>
                 `Weight "${afterItem['Weight']}", Height "${afterItem['Height Ft']}ft, ${afterItem['Height In']}in"`).join(', ')}"`
                : null;
            }
            return (
              <Fragment>
                {userName}
                {before === null &&
                  ' added '
                }
                {after === null &&
                  ' cleared '
                }
                {(before !== null && after !== null) &&
                  ' changed '
                }
                <span className={classes.fieldName}>
                  {fieldName}
                </span>
                {' '}
                {before !== null &&
                  <Fragment>
                    {after !== null && ` from `}
                    <span className={classes.fieldValue}>
                      {before}
                    </span>
                  </Fragment>       
                }
                {after !== null &&
                  <Fragment>
                    {before !== null && ` to `}
                    <span className={classes.fieldValue}>
                      {after}
                    </span>
                  </Fragment>       
                }
                {payerName && 
                  ` linked to ${payerName}`
                }
                {HCPCCode &&
                  ` in ${HCPCCode}`
                }
                {` in order `}
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${eventParams['Order ID']}`}
                  target="_blank"
                >
                  {`"${formatOrderNumber(eventParams['Order ID'])}"`}
                </Link>
              </Fragment>
            );
          };
          message = Object.keys(eventParams['Change Fields'])
            .filter(item =>
              item !== 'status'
              && item !== 'meta'
              && item !== 'main_code'
              && item !== 'medic_type')
            .reduce((accumulator, currentValue) => {
              if (currentValue === 'order_line') {
                const orderLineMessage = Object.keys(eventParams['Change Fields'][currentValue])
                  .filter(item =>
                    item !== 'status'
                    && item !== 'meta'
                    && item !== 'document_type')
                  .reduce((orderLineAccumulator, orderLineCurrentValue) => {
                    const HCPCCode = eventParams['Change Fields'][currentValue][orderLineCurrentValue].meta
                      ? eventParams['Change Fields'][currentValue][orderLineCurrentValue].meta['HCPC S Code']
                      : 'Order Line';
                    const orderLineFieldMessage = Object.keys(eventParams['Change Fields'][currentValue][orderLineCurrentValue])
                      .filter(item =>
                        item !== 'status'
                        && item !== 'meta'
                        && item !== 'product'
                        && item !== 'hcpc_code'
                        && (eventParams['Change Fields'][currentValue][orderLineCurrentValue].status !== 'create' || item !== 'is_rejected'))
                      .reduce((orderLineFieldAccumulator, orderLineFieldCurrentValue) => {
                        if (orderLineFieldCurrentValue === 'order_line_insurance_param') {
                          const orderLineInsuranceParamsMessage = Object.keys(eventParams['Change Fields'][currentValue][orderLineCurrentValue][orderLineFieldCurrentValue])
                            .filter(item => item !== 'status' && item !== 'meta')
                            .reduce((orderLineInsuranceParamsAccumulator, orderLineInsuranceParamsCurrentValue) => {
                              const payerName = eventParams['Change Fields'][currentValue][orderLineCurrentValue]
                                [orderLineFieldCurrentValue][orderLineInsuranceParamsCurrentValue].meta
                                ? eventParams['Change Fields'][currentValue][orderLineCurrentValue]
                                  [orderLineFieldCurrentValue][orderLineInsuranceParamsCurrentValue].meta['Payer Name']
                                : null;
                              const orderLineInsuranceParamsFiledMessage = Object.keys(eventParams['Change Fields'][currentValue][orderLineCurrentValue]
                                [orderLineFieldCurrentValue][orderLineInsuranceParamsCurrentValue])
                                .filter(item => item !== 'status' && item !== 'meta' && item !== 'insurance')
                                .reduce((orderLineInsuranceParamsFieldAccumulator, orderLineInsuranceParamsFieldCurrentValue) => {
                                  const fieldData = eventParams['Change Fields'][currentValue][orderLineCurrentValue]
                                    [orderLineFieldCurrentValue][orderLineInsuranceParamsCurrentValue][orderLineInsuranceParamsFieldCurrentValue];
                                  const currentMessage = getMessage({ field: orderLineInsuranceParamsFieldCurrentValue, fieldData, HCPCCode, payerName });
                                  return [
                                    ...orderLineInsuranceParamsFieldAccumulator,
                                    currentMessage,
                                  ];
                                }, []);
                              return [
                                ...orderLineInsuranceParamsAccumulator,
                                ...orderLineInsuranceParamsFiledMessage,
                              ];
                            }, []);
                          return [
                            ...orderLineFieldAccumulator,
                            ...orderLineInsuranceParamsMessage,
                          ];
                        }
                        const fieldData = eventParams['Change Fields'][currentValue][orderLineCurrentValue][orderLineFieldCurrentValue];
                        const currentMessage = getMessage({ field: orderLineFieldCurrentValue, fieldData, HCPCCode });
                        return [
                          ...orderLineFieldAccumulator,
                          currentMessage,
                        ];
                      }, [
                        ...eventParams['Change Fields'][currentValue][orderLineCurrentValue].status === 'create' ? 
                          [(<Fragment>
                            {`${userName}
                            created order line `}
                            <span className={classes.fieldValue}>
                            {`"${HCPCCode}"`}
                            </span>{` in order `}
                            <Link
                              color="primary"
                              breakWord
                              href={`/workflow/order/edit/${eventParams['Order ID']}`}
                              target="_blank"
                            >
                              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
                            </Link>
                          </Fragment>)] : [],
                        ...eventParams['Change Fields'][currentValue][orderLineCurrentValue].status === 'delete' ? 
                          [(<Fragment>
                            {`${userName}
                            deleted order line `}
                            <span className={classes.fieldValue}>
                            {`"${HCPCCode}"`}
                            </span>{` in order `}
                            <Link
                              color="primary"
                              breakWord
                              href={`/workflow/order/edit/${eventParams['Order ID']}`}
                              target="_blank"
                            >
                              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
                            </Link>
                          </Fragment>)] : [],
                      ]);
                    return [
                      ...orderLineAccumulator,
                      ...orderLineFieldMessage,
                    ];
                  }, []);
                return [
                  ...accumulator,
                  ...orderLineMessage,
                ];
              }

              if (currentValue === 'order_insurance_param') {
                const orderInsuranceParamMessage = Object.keys(eventParams['Change Fields'][currentValue])
                  .filter(item => item !== 'status' && item !== 'meta')
                  .reduce((orderInsuranceParamAccumulator, orderInsuranceParamCurrentValue) => {
                    const payerName = eventParams['Change Fields'][currentValue][orderInsuranceParamCurrentValue].meta
                      ? eventParams['Change Fields'][currentValue][orderInsuranceParamCurrentValue].meta['Payer Name']
                      : null;
                    const orderInsuranceParamFieldMessage = Object.keys(eventParams['Change Fields'][currentValue][orderInsuranceParamCurrentValue])
                      .filter(item => item !== 'status' && item !== 'meta' && item !== 'insurance')
                      .reduce((orderInsuranceParamFieldAccumulator, orderInsuranceParamFieldCurrentValue) => {
                        const fieldData = eventParams['Change Fields'][currentValue][orderInsuranceParamCurrentValue][orderInsuranceParamFieldCurrentValue];
                        const currentMessage = getMessage({ field: orderInsuranceParamFieldCurrentValue, fieldData, payerName });
                        return [
                          ...orderInsuranceParamFieldAccumulator,
                          currentMessage,
                        ];
                      }, []);
                    return [
                      ...orderInsuranceParamAccumulator,
                      ...orderInsuranceParamFieldMessage,
                    ];
                  }, [])
                return [
                  ...accumulator,
                  ...orderInsuranceParamMessage,
                ];
              }

              const fieldData = eventParams['Change Fields'][currentValue];
              const currentMessage = getMessage({ field: currentValue, fieldData });
              return [
                ...accumulator,
                currentMessage,
              ];
            }, [
              ...eventParams['Change Fields'].status === 'create' ? 
                [(<Fragment>
                  {`${userName}
                  created order `}
                  <Link
                    color="primary"
                    breakWord
                    href={`/workflow/order/edit/${eventParams['Order ID']}`}
                    target="_blank"
                  >
                    {`"${formatOrderNumber(eventParams['Order ID'])}"`}
                  </Link>
                </Fragment>)] : [],
            ]).reverse();
          }
        break;
      }
      case 'Add Resource order_diagnosis':
        message = (
          <Fragment>
            {`${userName}
            added diagnosis ${eventParams['Resource Info']['DX Code']}
            "${eventParams['Resource Info']['Medium Description']}"
            to order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Remove Resource order_diagnosis':
        message = (
          <Fragment>
            {`${userName}
            removed diagnosis ${eventParams['Resource Info']['DX Code']}
            "${eventParams['Resource Info']['Medium Description']}"
            from order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Add Resource order_vitals':
        message = (
          <Fragment>
            {`${userName}
            added vitals:
            weight "${eventParams['Resource Info'].Weight} lbs",
            height: "${eventParams['Resource Info']['Height Ft']}ft,
            ${eventParams['Resource Info']['Height In']}in",
            BMI "${eventParams['Resource Info'].BMI}"
            to order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Remove Resource order_vitals':
        message = (
          <Fragment>
            {`${userName}
            removed vitals:
            weight "${eventParams['Resource Info'].Weight} lbs",
            height: "${eventParams['Resource Info']['Height Ft']}ft,
            ${eventParams['Resource Info']['Height In']}in",
            BMI "${eventParams['Resource Info'].BMI}"
            from order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Add Resource order_sales_representative':
        message = (
          <Fragment>
            {`${userName} added sales representative
            "${eventParams['Resource Info']['First Name']} ${eventParams['Resource Info']['Last Name']}"
            to order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Remove Resource order_sales_representative':
        message = (
          <Fragment>
            {`${userName} removed sales representative
            "${eventParams['Resource Info']['First Name']} ${eventParams['Resource Info']['Last Name']}"
            from order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'Delete order': {
        message = `${userName}
        deleted order ${formatOrderNumber(eventParams['Order ID'])}`;
        break;
      }
      // Order Lines
      case 'Create order_line': {
        const orders = eventParams['Order ID'];
        const ordersLabel = formatSinglePlural({
          quantity: orders.length,
          single: 'order',
          plural: 'orders',
        });
        message = (
          <Fragment>
            {`${userName}
            created order line "${eventParams['HCPC S Code']}" in ${ordersLabel} `}
            {orders.map((item, index) => (
              <Fragment key={item}>
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${item}`}
                  target="_blank"
                >
                  {`"${formatOrderNumber(item)}"`}
                </Link>
                {index + 1 < orders.length ? ', ' : ''}
              </Fragment>
            ))}
          </Fragment>
        );
        break;
      }
      case 'Update order_line': {
        const fields = eventParams['Change Fields'].filter(item => item !== 'From User')
          .map((item) => {
            let fieldName = item;
            if (fieldName === 'assigned_user_id') {
              fieldName = 'Assigned User';
            }
            return `"${fieldName}"`;
          });
        const fieldsLabel = formatSinglePlural({
          quantity: fields.length,
          single: 'field',
          plural: 'fields',
        });
        if (fields[0] === '"Is Rejected"') {
          message = (
            <Fragment>
              {`${userName}
              updated Rejected status of order line "${eventParams['HCPC S Code']}"
              from order `}
              <Link
                color="primary"
                breakWord
                href={`/workflow/order/edit/${eventParams['Order ID']}`}
                target="_blank"
              >
                {`"${formatOrderNumber(eventParams['Order ID'])}"`}
              </Link>
            </Fragment>
          );
        } else {
          message = (
            <Fragment>
              {`${userName}
              updated ${fields.join(', ')} ${fieldsLabel} in order line "${eventParams['HCPC S Code']}"
              from order `}
              <Link
                color="primary"
                breakWord
                href={`/workflow/order/edit/${eventParams['Order ID']}`}
                target="_blank"
              >
                {`"${formatOrderNumber(eventParams['Order ID'])}"`}
              </Link>
            </Fragment>
          );
        }
        break;
      }
      case 'Delete order_line': {
        const orders = eventParams['Order ID'];
        const ordersLabel = formatSinglePlural({
          quantity: orders.length,
          single: 'order',
          plural: 'orders',
        });
        message = (
          <Fragment>
            {`${userName}
            deleted order line "${eventParams['HCPC S Code']}" from ${ordersLabel} `}
            {orders.map((item, index) => (
              <Fragment key={item}>
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${item}`}
                  target="_blank"
                >
                  {`"${formatOrderNumber(item)}"`}
                </Link>
                {index + 1 < orders.length ? ', ' : ''}
              </Fragment>
            ))}
          </Fragment>
        );
        break;
      }
      // Claims
      case 'Create claim':
        message = (
          <Fragment>
            {`${userName} created claim for order line "${
              eventParams['HCPC S Code']
            }", Date of Service "${eventParams['Date of Service From']} - ${
              eventParams['Date of Service To']
            }" from order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      case 'View claim':
        message = (
          <Fragment>
            {`${userName} viewed claim history for order line "${
              eventParams['HCPC S Code']
            }", Date of Service "${eventParams['Date of Service From']} - ${
              eventParams['Date of Service To']
            }" from order `}
            <Link
              color="primary"
              breakWord
              href={`/workflow/order/edit/${eventParams['Order ID']}`}
              target="_blank"
            >
              {`"${formatOrderNumber(eventParams['Order ID'])}"`}
            </Link>
          </Fragment>
        );
        break;
      default:
        message = Event;
    }
    const renderChangeItem = (changeItem) => {
      let renderText;
      switch (changeItem[0]) {
        case 'Text':
          renderText = `Note text from ${changeItem[1][0] ? `"${changeItem[1][0]}"` : 'empty'} to ${changeItem[1][1] ? `"${changeItem[1][1]}"` : 'empty'}`;
          break;
        default:
          renderText = `${renderFieldName(changeItem[0])} from ${changeItem[1][0] ? `"${changeItem[1][0]}"` : 'empty'} to ${changeItem[1][1] ? `"${changeItem[1][1]}"` : 'empty'}`;
      }
      return renderText;
    };
    const renderAddItem = (addItem) => {
      let renderText;
      switch (addItem[0]) {
        case 'Contact Information': {
          const notEmptyFields = get(addItem, '[1]', []).filter(item => item[1]);
          renderText = `Contact Information ${notEmptyFields.map(item => `"${item[0]}" with value "${item[1]}"`).join(', ')}`;
          break;
        }
        case 'Address Information': {
          const notEmptyFields = get(addItem, '[1]', []).filter(item => item[1]);
          renderText = `Address Information ${notEmptyFields.map(item => `"${item[0]}" with value "${item[1]}"`).join(', ')}`;
          break;
        }
        case 'Referral Source':
          renderText = `Referral Source "${get(addItem, '[1][0][1]["Name of source"]', '')}"`;
          break;
        case 'Sales Representative':
          renderText = `${addItem[0]} "${get(addItem, '[1][0][1]["First Name"]', '')} ${get(addItem, '[1][0][1]["Last Name"]', '')}"`;
          break;
        case 'Prescriber':
          renderText = `${addItem[0]} "${get(addItem, '[1][0][1]["First Name"]', '')} ${get(addItem, '[1][0][1]["Last Name"]', '')}" (NPI: ${get(addItem, '[1][0][1].NPI', '')})`;
          break;
        case 'Diagnosis':
          renderText = `${addItem[0]} "${get(addItem, '[1][0][1]["DX Code"]', '')} ${get(addItem, '[1][0][1]["Long Description"]', '')}"`;
          break;
        case 'Documents':
          renderText = `Document "${get(addItem, '[1][0][1]["File Name"]', '')}"`;
          break;
        case 'Workflow Status':
          renderText = `Workflow Status "${get(addItem, '[1][0][1]["Name"]', '')}"`;
          break;
        default:
          renderText = addItem.toString();
      }
      return renderText;
    };
    const renderRemoveItem = (removeItem) => {
      let renderText;
      switch (removeItem[0]) {
        case 'Referral Source':
          renderText = `${removeItem[0]} "${get(removeItem, '[1][0][1]["Name of source"]', '')}"`;
          break;
        case 'Sales Representative':
          renderText = `${removeItem[0]} "${get(removeItem, '[1][0][1]["First Name"]', '')} ${get(removeItem, '[1][0][1]["Last Name"]', '')}"`;
          break;
        case 'Prescriber':
          renderText = `${removeItem[0]} "${get(removeItem, '[1][0][1]["First Name"]', '')} ${get(removeItem, '[1][0][1]["Last Name"]', '')}" (NPI: ${get(removeItem, '[1][0][1].NPI', '')})`;
          break;
        case 'Diagnosis':
          renderText = `${removeItem[0]} "${get(removeItem, '[1][0][1]["DX Code"]', '')} ${get(removeItem, '[1][0][1]["Long Description"]', '')}"`;
          break;
        case 'Documents':
          renderText = `Document "${get(removeItem, '[1][0][1]["File Name"]', '')}"`;
          break;
        default:
          renderText = '';
      }
      return renderText;
    };
    if (diffOpen && Diff && Diff.Change && eventParams['Change Fields'][0] === 'assigned_user_id') {
      const firstName = Diff.Change.find(item => item[0] === 'Assigned User.First Name');
      const lastName = Diff.Change.find(item => item[0] === 'Assigned User.Last Name');
      if (firstName && lastName) {
        const newDiffChange = [[
          'Assigned User Name',
          [
            `${firstName[1][0]} ${lastName[1][0]}`,
            `${firstName[1][1]} ${lastName[1][1]}`,
          ],
        ]];

        Diff.Change = newDiffChange;
      }
    }
    if (newEventLogItem) {
      return (
        <Fragment>
          {message.map((currentMessage, index) => (
            <TableRow
              key={`${ID}-${index}`}
              className={classNames(classes.tableRow, {
                [classes.tableRowDiffOpen]: diffOpen && Diff,
              })}
            >
              <TableCell
                className={classNames(classes.tableCell, classes.tableCellDate, {
                  [classes.tableCellDiffOpen]: diffOpen && Diff,
                })}
              >
                {formatUtcToLocalDate(
                  otherProps['Create At'],
                  timeDateFormat,
                  timeDateFormat,
                  business['Company Address']['Time Zone'],
                  business['Company Address']['Observes Daylight Saving'],
                )}
              </TableCell>
              <TableCell
                className={classNames(classes.tableCell, {
                  [classes.tableCellDiffOpen]: diffOpen && Diff,
                })}
              >
                {currentMessage}
              </TableCell>
              <TableCell
                className={classNames(classes.tableCell, classes.tableCellActions)}
              />
            </TableRow>
          ))}     
        </Fragment>
      );
    }
    return (
      <Fragment>
        <TableRow
          className={classNames(classes.tableRow, {
            [classes.tableRowDiffOpen]: diffOpen && Diff,
          })}
        >
          <TableCell
            className={classNames(classes.tableCell, classes.tableCellDate, {
              [classes.tableCellDiffOpen]: diffOpen && Diff,
            })}
          >
            {formatUtcToLocalDate(
              otherProps['Create At'],
              timeDateFormat,
              timeDateFormat,
              business['Company Address']['Time Zone'],
              business['Company Address']['Observes Daylight Saving'],
            )}
          </TableCell>
          <TableCell
            className={classNames(classes.tableCell, {
              [classes.tableCellDiffOpen]: diffOpen && Diff,
            })}
          >
            {message}
          </TableCell>
          <TableCell
            className={classNames(classes.tableCell, classes.tableCellActions, {
              [classes.tableCellDiffOpen]: diffOpen && Diff,
            })}
          >
            {(otherProps['Diff ID'] && `${Event} ${eventParams.Type}` !== 'Update credit_card') &&
              <IconButton
                className={classes.button}
                onClick={this.onDiffButtonClick}
                tooltipContent="Show more"
              >
                <IconArrowHeadBottom
                  className={classNames(classes.iconArrowHeadBottom, {
                    [classes.iconArrowHeadBottomDiffOpen]: diffOpen && Diff,
                  })}
                />
              </IconButton>
            }
          </TableCell>
        </TableRow>
        {(diffOpen && Diff && Diff.Change) &&
          <TableRow className={classNames(classes.tableRow, classes.tableRowDiff)}>
            <TableCell className={classNames(classes.tableCellDiffType, classes.tableCellDiff)}>
              {Diff.Change.map((changeItem, index) => (
                <div
                  key={changeItem[0]}
                  className={classNames({
                    [classes.diffTypeHidden]: index !== 0,
                  })}
                >
                  Amended:
                </div>
              ))}
            </TableCell>
            <TableCell className={classes.tableCellDiff} colSpan={2}>
              {Diff.Change.map(changeItem => (
                <div key={changeItem[0]}>
                  {renderChangeItem(changeItem)}
                </div>
              ))}
            </TableCell>
          </TableRow>
        }
        {(diffOpen && Diff && Diff.Add) &&
          <TableRow className={classNames(classes.tableRow, classes.tableRowDiff)}>
            <TableCell className={classNames(classes.tableCellDiffType, classes.tableCellDiff)}>
              {Diff.Add.map((addItem, index) => (
                <div
                  key={addItem[0]}
                  className={classNames({
                    [classes.diffTypeHidden]: index !== 0,
                  })}
                >
                  Added:
                </div>
              ))}
            </TableCell>
            <TableCell className={classes.tableCellDiff} colSpan={2}>
              {Diff.Add.map(addItem => (
                <div key={addItem[0]}>
                  {renderAddItem(addItem)}
                </div>
              ))}
            </TableCell>
          </TableRow>
        }
        {(diffOpen && Diff && Diff.Remove && eventParams['Change Fields'][0] !== 'assigned_user_id') &&
          <TableRow className={classNames(classes.tableRow, classes.tableRowDiff)}>
            <TableCell className={classNames(classes.tableCellDiffType, classes.tableCellDiff)}>
              {Diff.Remove.map((removeItem, index) => (
                <div
                  key={removeItem[0]}
                  className={classNames({
                    [classes.diffTypeHidden]: index !== 0,
                  })}
                >
                  Removed:
                </div>
              ))}
            </TableCell>
            <TableCell className={classes.tableCellDiff} colSpan={2}>
              {Diff.Remove.map(removeItem => (
                <div key={removeItem[0]}>
                  {renderRemoveItem(removeItem)}
                </div>
              ))}
            </TableCell>
          </TableRow>
        }
      </Fragment>
    );
  }
}

EventLogViewTableRow.propTypes = {
  Event: PropTypes.string.isRequired,
  'Create At': PropTypes.string.isRequired,
  'Event Params': PropTypes.shape({
    'Change Fields': PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  User: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
  }).isRequired,
  'Diff ID': PropTypes.number,
  Diff: PropTypes.shape({
    ID: PropTypes.number.isRequired,
  }),
  actions: PropTypes.shape({
    fetchEventLogItemDiff: PropTypes.func.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  classes: PropTypes.shape({
    button: PropTypes.string.isRequired,
    iconArrowHeadBottom: PropTypes.string.isRequired,
    iconArrowHeadBottomDiffOpen: PropTypes.string.isRequired,
    tableCell: PropTypes.string.isRequired,
    tableCellDate: PropTypes.string.isRequired,
    tableCellDiffType: PropTypes.string.isRequired,
    tableCellActions: PropTypes.string.isRequired,
    tableRowDiffOpen: PropTypes.string.isRequired,
    tableCellDiffOpen: PropTypes.string.isRequired,
    tableRowDiff: PropTypes.string.isRequired,
    tableCellDiff: PropTypes.string.isRequired,
    diffTypeHidden: PropTypes.string.isRequired,
  }).isRequired,
};

EventLogViewTableRow.defaultProps = {
  'Diff ID': null,
  Diff: null,
  business: {},
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ fetchEventLogItemDiff }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withStyles(styles)(EventLogViewTableRow),
);



// WEBPACK FOOTER //
// ./src/components/patient/EventLogViewDialog/EventLogViewTableRow.js